import { createReducer, on } from '@ngrx/store';
import UserStoryActions from '../../actions/generations/userstory.actions';
import { generationUserStoryDefaults } from 'src/app/components/generations-wizard/user-story-generation/defaults';
import { GenerationActions } from '../../actions/generations/generation.actions';

interface Module {
  id: string;
  type: 'MODULE';
  name: string;
  [key: string]: any;
}

interface Process {
  id: string;
  type: 'PROCESS';
  name: string;
  [key: string]: any;
}

interface UserStory {
  id: string;
  type: 'USER_STORY';
  name: string;
  [key: string]: any;
}

interface UserStoryState {
  entities: {
    modules: Record<string, Module>;
    processes: Record<string, Process>;
    userStories: Record<string, UserStory>;
  };
  ids: {
    modules: string[];
    processes: string[];
    userStories: string[];
  };
}

const placeholderModuleId: string = 'placeholder_module';
const placeholderProcessId: string = 'placeholder_process';

const initialState: UserStoryState = {
  entities: {
    modules: {},
    processes: {},
    userStories: {}
  },
  ids: {
    modules: [],
    processes: [],
    userStories: []
  }
};

const userStoryReducer = createReducer(
  initialState,

  // Set Normalized Data
  on(UserStoryActions.setNormalizedData, (state, { data }) => {
    const entities: {
      modules: Record<string, Module>;
      processes: Record<string, Process>;
      userStories: Record<string, UserStory>;
    } = { modules: {}, processes: {}, userStories: {} };
    const ids: {
      modules: string[];
      processes: string[];
      userStories: string[];
    } = { modules: [], processes: [], userStories: [] };

    // Initialize placeholders
    // entities.modules[placeholderModuleId] = {
    //   id: placeholderModuleId,
    //   type: 'MODULE',
    //   children: [],
    //   name: 'Placeholder Module'
    // };
    // ids.modules.push(placeholderModuleId);

    // Normalize data
    data.forEach((module) => {
      const moduleId = module.sid || placeholderModuleId;
      if (!entities.modules[moduleId]) {
        entities.modules[moduleId] = {
          ...module,
          id: moduleId,
          type: 'MODULE',
          name: module.name || 'Placeholder Module',
          children: []
        };
        ids.modules.push(moduleId);
      }

      module.children?.forEach((process) => {
        const processId = process.sid || placeholderProcessId;
        if (!entities.processes[processId]) {
          entities.processes[processId] = {
            ...process,
            id: processId,
            type: 'PROCESS',
            name: process.name || 'Placeholder Process',
            children: []
          };
          ids.processes.push(processId);
        }
        entities.modules[moduleId].children.push(processId);

        process.children?.forEach((userStory) => {
          const userStoryId = userStory.sid || '';
          entities.userStories[userStoryId] = {
            ...generationUserStoryDefaults,
            ...userStory,
            id: userStoryId,
            type: 'USER_STORY',
            name: userStory.name || ''
          };
          ids.userStories.push(userStoryId);
          entities.processes[processId].children.push(userStoryId);
        });
      });
    });

    return {
      ...state,
      entities,
      ids
    };
  }),

  // Add User Story
  on(UserStoryActions.addUserStoryInState, (state, { data: us }) => {
    const { parentNodeSid: parentProcessId } = us;

    const userStory: UserStory = {
      ...generationUserStoryDefaults,
      ...us,
      id: us.sid
    };

    // Ensure parent process exists, or create a placeholder process
    const processId = parentProcessId || placeholderProcessId;
    let process: Process = state.entities.processes[processId];
    if (!process) {
      process = {
        id: processId,
        name: 'Placeholder Process',
        children: [],
        type: 'PROCESS' // Added type property
      };

      // Ensure placeholder module exists
      const moduleId = placeholderModuleId;
      let module = state.entities.modules[moduleId];
      if (!module) {
        module = {
          id: moduleId,
          name: 'Placeholder Module',
          children: [],
          type: 'MODULE' // Added type property
        };

        return {
          ...state,
          entities: {
            ...state.entities,
            modules: {
              ...state.entities.modules,
              [moduleId]: module
            },
            processes: {
              ...state.entities.processes,
              [processId]: process
            },
            userStories: {
              ...state.entities.userStories,
              [userStory.sid]: userStory
            }
          },
          ids: {
            ...state.ids,
            modules: [...state.ids.modules, moduleId],
            processes: [...state.ids.processes, processId],
            userStories: [...state.ids.userStories, userStory.id]
          }
        };
      }

      // Add the placeholder process to the module
      module = {
        ...module,
        children: [...module.children, processId]
      };

      return {
        ...state,
        entities: {
          ...state.entities,
          modules: {
            ...state.entities.modules,
            [moduleId]: module
          },
          processes: {
            ...state.entities.processes,
            [processId]: process
          },
          userStories: {
            ...state.entities.userStories,
            [userStory.id]: userStory
          }
        },
        ids: {
          ...state.ids,
          processes: [...state.ids.processes, processId],
          userStories: [...state.ids.userStories, userStory.id]
        }
      };
    }

    // Attach the user story to the process
    const updatedProcess = {
      ...process,
      children: [...process.children, userStory.id]
    };

    return {
      ...state,
      entities: {
        ...state.entities,
        processes: {
          ...state.entities.processes,
          [processId]: updatedProcess
        },
        userStories: {
          ...state.entities.userStories,
          [userStory.id]: userStory
        }
      },
      ids: {
        ...state.ids,
        userStories: [...state.ids.userStories, userStory.id]
      }
    };
  }),

  // Update User Story
  on(UserStoryActions.updateUserStoryInState, (state, { data: changes }) => {
    const { sid: id } = changes;

    // Ensure the user story exists
    const existingUserStory = state.entities.userStories[id];
    if (!existingUserStory) {
      console.warn(`[updateUserStory] User story with id ${id} not found.`);
      return state;
    }

    // Update the user story
    const updatedUserStory = {
      ...existingUserStory,
      ...changes
    };

    return {
      ...state,
      entities: {
        ...state.entities,
        userStories: {
          ...state.entities.userStories,
          [id]: updatedUserStory
        }
      }
    };
  }),

  // Delete User Story
  on(UserStoryActions.deleteUserStoryInState, (state, { id }) => {
    // Ensure the user story exists
    const existingUserStory = state.entities.userStories[id];
    if (!existingUserStory) {
      console.warn(`[deleteUserStory] User story with id ${id} not found.`);
      return state;
    }

    // Find the parent process
    const parentProcess = Object.values(state.entities.processes).find((process) => process.children.includes(id));

    if (!parentProcess) {
      console.warn(`[deleteUserStory] Parent process for user story with id ${id} not found.`);
      return state;
    }

    // Remove the user story from the parent process
    const updatedParentProcess = {
      ...parentProcess,
      children: parentProcess.children.filter((childId) => childId !== id)
    };

    // Remove the user story and update the state
    const { [id]: removedUserStory, ...remainingUserStories } = state.entities.userStories;

    return {
      ...state,
      entities: {
        ...state.entities,
        processes: {
          ...state.entities.processes,
          [parentProcess.id]: updatedParentProcess
        },
        userStories: remainingUserStories
      },
      ids: {
        ...state.ids,
        userStories: state.ids.userStories.filter((userStoryId) => userStoryId !== id)
      }
    };
  }),
  on(UserStoryActions.resetUserStoriesState, () => initialState),
  on(GenerationActions.resetGeneration, () => initialState)
);

export { userStoryReducer, UserStoryState };
